
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import "./OrderNew.css";
import { useEffect, useRef, useState } from "react";
import { offerToOpenCash } from "../../../components/Custom/CashFlow/Cash/offerToOpenCash";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, sendToApi } from "../../../../utils/ApiUtils";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../../../constants/Api";
import { formatDateTimeLocalInput, formatMonthYearLabel, generateDateUTC, parseDateTimeLocalToUTC } from "../../../../utils/DateUtils";
import { warnAlert, warnAlertYesOrNo } from "../../../components/alert/defaultAlert";
import { isInPreviousMonth } from "./appointmentSaleDate";
import ModalPageEvent from "../../../components/Common/Modal/ModalPageEvent";
import { Button, Col, FormGroup, Row } from "react-bootstrap";
import PaymentMethodSelect from "../../../components/Custom/Common/PaymentMethod/PaymentMethodSelect";
import InputDecimal from "../../../components/Forms/Element/InputDecimal";
import { convertDoubleToStringPTBR, convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import PaymentMethod from "../../../components/Custom/Common/PaymentMethod/PaymentMethod";
import CardFlagSelect from "../../../components/Custom/Main/CardFlag/CardFlagSelect";
import axiosInstance from "../../../../services/AxiosInstance";
import { ApiError } from "../../../components/Common/ApiError";
import { useBranch } from "../../../../context/BranchContext";
import CustomerQuickCreateModal from "../../../components/Custom/Main/Customer/CustomerQuickCreateModal";
import { OrderDiscountType } from "../../../../utils/OrderDiscountUtils";

/**
 * Teto de formas por venda.
 *
 * Duas cobrem o caso real (parte no cartão, parte em dinheiro) e mantêm o modal legível. O
 * backend aceita quantas formas vierem em Order.payments, então este limite é só da tela:
 * para permitir mais, basta aumentar o número.
 */
const MAX_PAYMENT_ROWS = 2;

let paymentRowSequence = 0;

/**
 * Uma forma de pagamento da venda.
 *
 * value só é usado quando o pagamento é dividido: com uma forma só, ela cobre o total da
 * venda e o campo nem aparece na tela, para o caminho comum continuar sendo um clique.
 */
const buildPaymentRow = (overrides = {}) => ({
    key: `payment-${paymentRowSequence++}`,
    method: null,
    value: '0,00',
    cardFlag: null,
    receivedMoney: '0,00',
    moneyChange: '0,00',
    ...overrides
});

const parseDecimal = (value) => parseFloat(convertStringToStringDouble(String(value ?? '0'))) || 0;

const isCashRow = (row) => row?.method?.value === PaymentMethod.CASH;

const isCardRow = (row) => row?.method?.value === PaymentMethod.CREDIT_CARD
    || row?.method?.value === PaymentMethod.DEBIT_CARD;

// Acento visual da linha, para o operador identificar a forma sem ler o texto do select.
const getPaymentRowAccent = (row) => {
    switch (row?.method?.value) {
        case PaymentMethod.CASH: return 'cash';
        case PaymentMethod.CREDIT_CARD:
        case PaymentMethod.DEBIT_CARD: return 'card';
        case PaymentMethod.PIX: return 'pix';
        case PaymentMethod.SUBSCRIPTION: return 'subscription';
        default: return null;
    }
};

const getPaymentRowIcon = (row) => {
    switch (row?.method?.value) {
        case PaymentMethod.CASH: return 'fas fa-money-bill-wave';
        case PaymentMethod.CREDIT_CARD:
        case PaymentMethod.DEBIT_CARD: return 'fas fa-credit-card';
        case PaymentMethod.PIX: return 'fas fa-bolt';
        case PaymentMethod.SUBSCRIPTION: return 'fas fa-repeat';
        default: return null;
    }
};

const OrderNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [entityData, setEntityData] = useState(null);
    const [items, setItems] = useState([]);
    const [resetForm, setResetForm] = useState(0);
    const [formVersion, setFormVersion] = useState(0);
    // Formas de pagamento da venda. Quase sempre é uma só, mas o cliente pode dividir (parte
    // no cartão, parte em dinheiro). O backend já modelava isso em Order.payments; a tela é
    // que só sabia enviar uma forma.
    const [payments, setPayments] = useState([buildPaymentRow()]);
    const [orderTotalValue, setOrderTotalValue] = useState(0);
    const [planLookupState, setPlanLookupState] = useState('ready');
    const planLookupMessage = planLookupState === 'error'
        ? t('plan_lookup_error')
        : t('plan_lookup_loading');
    // Desconto da venda calculado pelo Form ({ value, error }). orderTotalValue já vem com ele
    // descontado; aqui ele serve para o modal de pagamento e para barrar desconto inválido.
    const [orderDiscount, setOrderDiscount] = useState({ value: 0, error: null });
    const navigate = useNavigate();
    const { appointmentUuid } = useParams();
    // Se a empresa barra venda como Assinatura para quem está devendo mensalidade. Padrão do
    // servidor é barrar; a tela só avisa sobre atraso quando isso está ligado.
    const [blockSubscriptionSaleForDelinquent, setBlockSubscriptionSaleForDelinquent] = useState(true);
    // INFO(Devs): Data operacional da venda; pode ser retroativa e é diferente do createdAt/auditoria.
    const [initialRequestDate, setInitialRequestDate] = useState(formatDateTimeLocalInput());
    // INFO(Devs): Estado para armazenar o ID do agendamento vinculado, caso a venda venha de um agendamento.
    const [appointmentId, setAppointmentId] = useState(null);
    const [quickCustomerOpen, setQuickCustomerOpen] = useState(false);

    // Modal
    const [showConfirm, setShowConfirm] = useState(false);
    // Venda sendo gravada. O ref barra o segundo clique no mesmo instante (o estado só muda no
    // próximo render); o estado desabilita o botão. Dois cliques em "Confirmar pagamento" geravam
    // duas vendas iguais para o mesmo atendimento.
    const sendingRef = useRef(false);
    const [sending, setSending] = useState(false);

    // Soma o valor que o plano do clube absorveu nos itens da venda: a diferença entre o
    // preço cheio (originalPrice) e o preço já com o benefício aplicado. Item 100% incluso
    // entra com o preço cheio inteiro; item com desconto parcial entra só com o desconto.
    const calculatePlanCoveredValue = () => items.reduce((total, item) => {
        if (item.isSubscription !== true) {
            return total;
        }

        const qty = parseFloat(item.qty) || 0;
        const fullPrice = parseFloat(item.originalPrice != null ? item.originalPrice : item.price) || 0;
        const chargedPrice = parseFloat(item.price) || 0;

        return total + (Math.max(fullPrice - chargedPrice, 0) * qty);
    }, 0);

    // Valor absorvido pelo plano, usado só para informar o operador no modal de pagamento.
    const planCoveredValue = calculatePlanCoveredValue();

    // Quantidade de itens beneficiados pelo plano. É o que sempre dá para afirmar: o item
    // vindo de um agendamento já chega com o preço acertado pelo backend (originalPrice
    // igual ao preço cobrado), então nesse caminho não há como medir o valor absorvido.
    const planCoveredItemsCount = items.filter(item => item.isSubscription === true).length;

    // Indica que a venda tem participação do plano do clube (algum item veio marcado como
    // benefício da assinatura, seja gratuidade total ou desconto parcial).
    const hasPlanItems = planCoveredItemsCount > 0;

    // INFO(Devs): a trava em "somente assinatura" olha o VALOR a receber, não a flag do item.
    // Item com desconto parcial também vem com isSubscription = true e continua tendo valor a
    // pagar; travar pela flag fechava a venda como assinatura e o dinheiro nunca chegava ao
    // fluxo de caixa (TransactionCashFlowService ignora SUBSCRIPTION).
    const isPaidOnlyByPlan = hasPlanItems && orderTotalValue <= 0;

    // Sobrou valor a receber em uma forma de pagamento comum, além do que o plano cobriu.
    const hasAmountDueBeyondPlan = hasPlanItems && orderTotalValue > 0;

    // Reserva paga no agendamento de origem. O servidor é quem abate de fato (a venda nunca passa
    // do total); aqui só se calcula o mesmo número para o operador ver o que ainda falta receber.
    // Só vale reserva em situação PAID: retida (cliente perdeu o horário) ou já abatida não desconta.
    const [appointmentDeposit, setAppointmentDeposit] = useState(null);
    const depositApplied = appointmentDeposit?.situation === 'PAID'
        ? Math.min(Number(appointmentDeposit.value || 0), Math.max(orderTotalValue, 0))
        : 0;
    // O que o cliente ainda paga agora; as formas de pagamento cobrem este valor, e não o total.
    const payableValue = Math.round((orderTotalValue - depositApplied) * 100) / 100;
    // Reserva cobre a venda inteira (e há algo a abater): não há forma de pagamento a escolher.
    const coveredByDeposit = depositApplied > 0 && payableValue <= 0.01;

    useEffect(() => {
        if (!appointmentId) {
            setAppointmentDeposit(null);
            return;
        }
        axiosInstance.get(`${Api.appointment.deposit}/appointment/${appointmentId}`)
            .then((resp) => setAppointmentDeposit(resp.status === 204 ? null : resp.data))
            .catch(() => setAppointmentDeposit(null));
    }, [appointmentId]);

    useEffect(() => {
        axiosInstance.get(Api.parameter.sales)
            .then((resp) => {
                setBlockSubscriptionSaleForDelinquent(resp.data?.[0]?.blockSubscriptionSaleForDelinquent !== false);
            })
            .catch(error => ApiError(error));
    }, []);

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        customer: Yup.object().nullable().optional(),
        // Diferenças de relógio entre o terminal e o servidor não podem impedir a venda.
        requestDate: Yup.string().required(t('validation.required.date')),
        obs: Yup.string().nullable().optional(),
        employee: Yup.object().nullable().optional(),
        branch: Yup.object().nullable()
            .required(t('validation.required.branch'))
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            customer: null,
            requestDate: initialRequestDate,
            obs: '',
            employee: null,
            branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null,
            offDayDiscountOverride: false,
            // Desconto da venda: começa em percentual e vazio, que é "sem desconto".
            manualDiscountType: OrderDiscountType.PERCENTAGE,
            manualDiscountInput: ''
        },
        enableReinitialize: true, // Importante para atualizar se o selectedBranch mudar
        validationSchema: formSchema,
        onSubmit: () => {
            if (planLookupState !== 'ready') {
                warnAlert(planLookupMessage);
                formik.setSubmitting(false);
                return false;
            }
            // Verifica se existem itens.
            if (items.length === 0) {
                warnAlert(t("validation.required.order_item"));
                return false;
            }

            // Desconto fora dos limites (100% ou mais, valor maior que o total) para aqui, antes
            // do modal de pagamento: o servidor recusaria a venda do mesmo jeito.
            if (orderDiscount.error) {
                warnAlert(t(orderDiscount.error));
                formik.setSubmitting(false);
                return false;
            }

            initialDataPaymentMethodDialog();

            // Deixa habilitado o botão de submit.
            formik.setSubmitting(false);
        },
    });

    const initialDataPaymentMethodDialog = () => {
        setShowConfirm(true);

        // Reabrir o modal recomeça do zero, com uma única forma de pagamento cobrindo o total.
        // Só pré-seleciona assinatura quando não restou nada a receber: com valor em aberto o
        // operador precisa escolher em que forma o restante será pago.
        setPayments([buildPaymentRow({
            method: isPaidOnlyByPlan ? { value: PaymentMethod.SUBSCRIPTION, label: t('subscription') } : null,
            value: convertDoubleToStringPTBR(payableValue)
        })]);
    }

    // Soma das formas informadas. Com uma forma só, ela cobre o total por definição.
    const paymentsTotal = payments.length === 1
        ? payableValue
        : payments.reduce((total, row) => total + parseDecimal(row.value), 0);

    // Quanto ainda falta distribuir entre as formas. Serve para o aviso na tela e para
    // sugerir o valor da próxima forma adicionada.
    const remainingToSplit = Math.round((payableValue - paymentsTotal) * 100) / 100;

    const isSplitPayment = payments.length > 1;

    const updatePaymentRow = (key, patch) => {
        setPayments((currentRows) => currentRows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };

    /**
     * Divide o pagamento: a primeira forma passa a valer o que já foi digitado (ou o total,
     * quando ainda era a única) e a nova nasce com o restante, que é o caso comum — o cliente
     * diz quanto quer no cartão e o resto vai no dinheiro.
     */
    const addPaymentRow = () => {
        setPayments((currentRows) => {
            if (currentRows.length >= MAX_PAYMENT_ROWS) {
                return currentRows;
            }

            const isFirstSplit = currentRows.length === 1;
            const firstRowReceived = isCashRow(currentRows[0])
                ? parseDecimal(currentRows[0].receivedMoney)
                : 0;
            // Quando o operador já informou quanto recebeu em dinheiro, esse valor é a
            // melhor intenção para a primeira parte da divisão. Assim, ao adicionar Pix
            // depois de receber R$ 20, a tela sugere R$ 20 em dinheiro + o restante no Pix,
            // em vez de manter R$ 50 em dinheiro e criar um troco negativo artificial.
            const normalizedRows = isFirstSplit
                ? [{
                    ...currentRows[0],
                    value: firstRowReceived > 0 && firstRowReceived <= payableValue
                        ? convertDoubleToStringPTBR(firstRowReceived)
                        : convertDoubleToStringPTBR(payableValue)
                }]
                : currentRows;

            const distributed = normalizedRows.reduce((total, row) => total + parseDecimal(row.value), 0);
            const remaining = Math.max(Math.round((payableValue - distributed) * 100) / 100, 0);

            return [...normalizedRows, buildPaymentRow({ value: convertDoubleToStringPTBR(remaining) })];
        });
    };

    const removePaymentRow = (key) => {
        setPayments((currentRows) => (currentRows.length > 1
            ? currentRows.filter((row) => row.key !== key)
            : currentRows));
    };

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const onReset = () => {
        // INFO(Devs): Depois de salvar uma venda, a próxima deve iniciar com a data/hora atual.
        const nextRequestDate = formatDateTimeLocalInput();
        setInitialRequestDate(nextRequestDate);
        formik.resetForm({
            values: {
                customer: null,
                requestDate: nextRequestDate,
                obs: '',
                employee: null,
                branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null,
                offDayDiscountOverride: false,
                manualDiscountType: OrderDiscountType.PERCENTAGE,
                manualDiscountInput: ''
            }
        });

        setPayments([buildPaymentRow()]);
        setOrderTotalValue(0);
        setItems([]);
        setFormVersion((currentVersion) => currentVersion + 1);

        // Muda toda o valor para forçar o useEffect no form.
        // Ao detectar mudança no valor do resetForm, ele limpa os itens do pedido.
        setResetForm((currentReset) => currentReset + 1);
    }

    // INFO(Devs): O backend é a fonte única do desconto do plano. Por isso enviamos o preço CHEIO
    // (originalPrice) no campo price; o desconto é (re)calculado uma única vez no backend.
    // O price exibido no carrinho continua sendo a prévia com desconto (não vai no payload).
    const toBackendItems = (list) => (list || []).map((it) => ({
        ...it,
        price: it.originalPrice != null ? it.originalPrice : it.price
    }));

    const onConfirm = () => {
        if (planLookupState !== 'ready') {
            warnAlert(planLookupMessage);
            return false;
        }
        // Verifica se a filial está selecionada.
        if (!formik.values.branch) {
            warnAlert(t("validation.required.branch"));
            return false;
        }

        // Verifica se foi informada a forma de pagamento em todas as linhas.
        // Reserva que cobre a venda inteira dispensa a escolha da forma de pagamento.
        if (!coveredByDeposit && payments.some((row) => !row.method)) {
            warnAlert(t("validation.required.payment_method"));
            return false;
        }

        // No pagamento dividido, a soma das formas precisa fechar o total: o backend recusa a
        // venda quando não fecha, então o operador precisa saber disso antes de confirmar.
        if (isSplitPayment && Math.abs(remainingToSplit) > 0.01) {
            warnAlert(t("validation.invalid.payments_total", {
                total: convertDoubleToStringPTBR(payableValue),
                informed: convertDoubleToStringPTBR(paymentsTotal)
            }));
            return false;
        }

        for (const row of (coveredByDeposit ? [] : payments)) {
            const rowValue = payments.length === 1 ? payableValue : parseDecimal(row.value);

            if (isSplitPayment && rowValue <= 0) {
                warnAlert(t("validation.invalid.payment_value"));
                return false;
            }

            if (isCashRow(row)) {
                // Impede que seja informado um valor, em dinheiro, menor que o que a forma cobre.
                const receivedValue = parseDecimal(row.receivedMoney);

                if (receivedValue === 0) {
                    warnAlert(t("validation.required.received_money"));
                    return false;
                }

                if (receivedValue < rowValue) {
                    warnAlert(t("validation.invalid.received_money"));
                    return false;
                }
            }

            if (isCardRow(row) && !row.cardFlag) {
                warnAlert(t("validation.required.card_flag"));
                return false;
            }
        }

        // Uma forma só mantém o formato antigo do payload (campos únicos da venda). Com duas ou
        // mais, vai o array payments, e o backend deriva a venda como pagamento misto.
        // Com a reserva cobrindo tudo, a venda segue como RESERVATION e o servidor monta o
        // pagamento; não há linha de forma de pagamento para ler.
        const firstRow = coveredByDeposit
            ? { method: { value: PaymentMethod.RESERVATION } }
            : payments[0];
        const receivedMoneyValue = !coveredByDeposit && !isSplitPayment && isCashRow(firstRow)
            ? parseDecimal(firstRow.receivedMoney)
            : null;
        const moneyChangeValue = !coveredByDeposit && !isSplitPayment && isCashRow(firstRow)
            ? parseDecimal(firstRow.moneyChange)
            : null;
        const cardFlagValue = !coveredByDeposit && !isSplitPayment && isCardRow(firstRow) && firstRow.cardFlag
            ? { id: firstRow.cardFlag.value }
            : null;

        const paymentsPayload = isSplitPayment && !coveredByDeposit
            ? payments.map((row) => ({
                paymentMethod: row.method.value,
                value: parseDecimal(row.value),
                cardFlag: isCardRow(row) && row.cardFlag ? { id: row.cardFlag.value } : null,
                receivedMoney: isCashRow(row) ? parseDecimal(row.receivedMoney) : null,
                moneyChange: isCashRow(row) ? parseDecimal(row.moneyChange) : null
            }))
            : [];

        let customer = null;
        if (formik.values.customer !== undefined && formik.values.customer !== null) {
            customer = {
                id: formik.values.customer.value
            }
        }

        let employee = null;
        if (formik.values.employee !== undefined && formik.values.employee !== null) {
            employee = {
                id: formik.values.employee.value
            }
        }

        let payload = {
            id: getId(entityData),
            customer: customer,
            // Com pagamento dividido o backend sobrescreve para MISTO ao derivar de payments;
            // o campo continua sendo enviado porque a validação de assinatura o lê antes disso.
            paymentMethod: firstRow.method.value,
            payments: paymentsPayload,
            obs: formik.values.obs ? formik.values.obs : null,
            items: toBackendItems(items),
            // INFO(Devs): Usa a data escolhida pelo usuário para relatórios, caixa e numeração diária.
            requestDate: parseDateTimeLocalToUTC(formik.values.requestDate) || generateDateUTC(),
            employee: employee,
            moneyChange: moneyChangeValue,
            receivedMoney: receivedMoneyValue,
            cardFlag: cardFlagValue,
            branch: {
                id: formik.values.branch.value
            },
            // INFO(Devs): Vincula a venda ao agendamento, se existir. 
            // O OrderController no backend se encarregará de transformar em entidade.
            appointment: appointmentId ? { id: appointmentId } : null
            , offDayDiscountOverride: Boolean(formik.values.offDayDiscountOverride)
            // Vai o que o operador informou (tipo e número). O valor em reais e a divisão entre
            // os itens são recalculados pelo servidor. Sem desconto efetivo, nada é enviado.
            , manualDiscountType: orderDiscount.value > 0 ? formik.values.manualDiscountType : null
            , manualDiscountInput: orderDiscount.value > 0 ? parseDecimal(formik.values.manualDiscountInput) : null
        }

        // Verifica se alguma das formas é Assinatura e o cliente não possui plano ativo. Olha
        // todas as linhas porque, no pagamento dividido, a assinatura pode ser a segunda.
        if (!coveredByDeposit && payments.some((row) => row.method.value === PaymentMethod.SUBSCRIPTION)) {
            // clubMember significa "tem contrato já pago": o inadimplente continua sendo do
            // clube e continua usando o plano, e quem nunca pagou não é do clube. Por isso as duas
            // situações são avisos diferentes — juntá-las fazia a tela dizer que um assinante
            // em atraso "não possui assinatura", o que manda o operador procurar um problema
            // de cadastro que não existe.
            const selectedCustomer = formik.values.customer?.customer;
            const isClubMember = Boolean(selectedCustomer?.clubMember);
            const isClubLate = Boolean(selectedCustomer?.clubLate);

            if (!isClubMember) {
                warnAlertYesOrNo(t('message.subscription_payment_without_active_subscription'), () => {
                    executeConfirm(payload);
                });
                return;
            }

            // Atraso só vira aviso quando a empresa optou por barrar o devedor (mesmo
            // parâmetro que o servidor usa em OrderService). Com o parâmetro desligado, o
            // assinante em atraso fecha como Assinatura sem interrupção, que é a regra: a
            // cobrança é tratada pela régua de cobrança, não no balcão.
            if (isClubLate && blockSubscriptionSaleForDelinquent) {
                warnAlertYesOrNo(t('message.subscription_payment_with_late_subscription'), () => {
                    executeConfirm(payload);
                });
                return;
            }

            // O plano já cobriu os itens dele; o que sobrou são itens fora do plano (ou com
            // desconto parcial). Fechar esse restante como assinatura não gera lançamento no
            // fluxo de caixa, então o operador confirma que é isso mesmo antes de gravar.
            if (hasAmountDueBeyondPlan) {
                warnAlertYesOrNo(t('message.subscription_payment_with_amount_due'), () => {
                    executeConfirm(payload);
                });
                return;
            }
        }

        executeConfirm(payload);
    }

    // Venda de atendimento de mês anterior entra naquele mês, que pode já ter comissão e Pote fechados.
    // É o único caso em que a data do atendimento muda um fechamento, por isso pergunta antes de gravar.
    const executeConfirm = (payload) => {
        const requestDate = formik.values.requestDate;
        if (entityData == null && appointmentId && isInPreviousMonth(requestDate)) {
            const month = formatMonthYearLabel(Number(requestDate.slice(5, 7)), Number(requestDate.slice(0, 4)));
            warnAlertYesOrNo(t('message.sale_enters_appointment_month', { month }), () => submitOrder(payload));
            return;
        }
        submitOrder(payload);
    };

    const submitOrder = (payload) => {
        if (sendingRef.current) {
            return;
        }
        sendingRef.current = true;
        setSending(true);
        sendToApi(Api.sales.order, payload, (resp) => {
            if (entityData == null) {
                setShowConfirm(false);

                // A impressão é um passo posterior à gravação; o formulário já deve ficar
                // limpo para não reapresentar os itens da venda recém-confirmada.
                onReset();
                if (appointmentUuid) {
                    // Vendas comuns permanecem no caixa; as originadas na agenda voltam ao quadro por colaborador.
                    navigate(MenuConfig.appointment.dashboard.route);
                }
            } else {
                navigate(MenuConfig.sales.order.route);
            }
        }, error => {
            // A venda só pode criar o caixa após confirmação explícita, usando a filial da
            // própria venda e saldo inicial zero; outros erros seguem o tratamento normal.
            const branchId = payload.branch?.id
                || formik.values.branch?.id
                || formik.values.branch?.value;

            const handled = offerToOpenCash({
                error,
                branchId,
                question: t('cash_flow.open_cash_before_sale'),
                openingNote: t('cash_flow.automatic_opening_for_sale'),
                // O erro acontece durante o modal de confirmação do pagamento, que precisa sair
                // da frente do alerta.
                onBeforeAsk: () => setShowConfirm(false),
                onOpened: () => submitOrder(payload)
            });
            if (!handled) {
                ApiError(error);
            }
        }, () => {
            // Resposta recebida (sucesso ou erro): libera o botão para uma nova tentativa.
            sendingRef.current = false;
            setSending(false);
        });
    }

    return (
        <>
            <ModalPageEvent
                title={
                    <div className="d-flex align-items-center">
                        <i className="fas fa-credit-card fa-lg me-3"></i>
                        {t('payment_method')}
                    </div>
                }
                show={showConfirm}
                setShow={setShowConfirm}
                child={
                    <div className="order-payment-modal">
                        <div className="order-payment-modal__total">
                            <small className="order-payment-modal__total-label">
                                {hasAmountDueBeyondPlan || depositApplied > 0 ? t('amount_due').toUpperCase() : t('order_total').toUpperCase()}
                            </small>
                            <h1 className="order-payment-modal__total-value">
                                {t('currency_symbol')} {convertDoubleToStringPTBR(payableValue)}
                            </h1>
                        </div>

                        {
                            // Reserva paga no agendamento: mostra o quanto já foi pago, para o
                            // operador entender por que o valor a receber é menor que o total.
                            depositApplied > 0 ?
                                <div className="order-payment-modal__plan-summary">
                                    <span className="order-payment-modal__plan-summary-label">
                                        <i className="fas fa-shield-halved me-2"></i>
                                        {t('deposit_discounted')}
                                    </span>
                                    <strong className="order-payment-modal__plan-summary-value">
                                        - {t('currency_symbol')} {convertDoubleToStringPTBR(depositApplied)}
                                    </strong>
                                </div>
                                :
                                <></>
                        }

                        {
                            // Desconto dado na venda, para o operador conferir que o valor a
                            // receber acima já está com ele descontado.
                            orderDiscount.value > 0 ?
                                <div className="order-payment-modal__plan-summary">
                                    <span className="order-payment-modal__plan-summary-label">
                                        <i className="fas fa-tag me-2"></i>
                                        {t('discount')}
                                    </span>
                                    <strong className="order-payment-modal__plan-summary-value">
                                        - {t('currency_symbol')} {convertDoubleToStringPTBR(orderDiscount.value)}
                                    </strong>
                                </div>
                                :
                                <></>
                        }

                        {
                            // Deixa explícito o quanto o plano do clube absorveu, para o operador
                            // entender por que o valor a receber é menor que a soma dos itens.
                            hasPlanItems ?
                                <div className="order-payment-modal__plan-summary">
                                    <span className="order-payment-modal__plan-summary-label">
                                        <i className="fas fa-crown me-2"></i>
                                        {t('covered_by_plan')} ({planCoveredItemsCount})
                                    </span>
                                    {
                                        // Só exibe o valor quando ele é mensurável (item incluído
                                        // no carrinho com preço cheio conhecido); no item vindo do
                                        // agendamento fica só a contagem, sem número inventado.
                                        planCoveredValue > 0 ?
                                            <strong className="order-payment-modal__plan-summary-value">
                                                {t('currency_symbol')} {convertDoubleToStringPTBR(planCoveredValue)}
                                            </strong>
                                            :
                                            <></>
                                    }
                                </div>
                                :
                                <></>
                        }

                        {coveredByDeposit ?
                            <div className="order-payment-modal__plan-summary">
                                <span className="order-payment-modal__plan-summary-label">
                                    <i className="fas fa-circle-check me-2"></i>
                                    {t('deposit_covers_order')}
                                </span>
                            </div>
                            :
                        <div className="order-payment-modal__panel order-payment-modal__panel--selector mb-4">
                            <div className="order-payment-modal__panel-header">
                                <span className="order-payment-modal__panel-icon">
                                    <i className="fas fa-wallet"></i>
                                </span>
                                <div>
                                    <h6 className="order-payment-modal__panel-title mb-1">
                                        {hasAmountDueBeyondPlan ? t('select_payment_method_remaining') : t('select_payment_method')}
                                    </h6>
                                </div>
                            </div>

                            {payments.map((row, index) => {
                                const accent = getPaymentRowAccent(row);
                                const rowIcon = getPaymentRowIcon(row);

                                return (
                                    <div
                                        key={row.key}
                                        className={`payment-row ${isSplitPayment ? 'payment-row--card' : ''} ${accent ? `payment-row--accent-${accent}` : ''}`}
                                    >
                                        {
                                            // O círculo numerado só faz sentido quando há mais de
                                            // uma forma para distinguir.
                                            isSplitPayment ?
                                                <span className="payment-row__index">
                                                    {rowIcon ? <i className={rowIcon}></i> : index + 1}
                                                </span>
                                                :
                                                <></>
                                        }
                                        <div className="payment-row__body">
                                            <Row className="g-2">
                                                <Col className={isSplitPayment ? 'col-md-7' : 'col-md-12'}>
                                                    <FormGroup className="mb-0">
                                                        {
                                                            isSplitPayment ?
                                                                <span className="payment-row__label">{t('payment_method')}</span>
                                                                :
                                                                <></>
                                                        }
                                                        <PaymentMethodSelect
                                                            // Sem o value o campo não exibia a assinatura pré-selecionada
                                                            // pelo initialDataPaymentMethodDialog, dando a impressão de
                                                            // que nada tinha sido escolhido.
                                                            value={row.method}
                                                            onChange={(value) => updatePaymentRow(row.key, {
                                                                method: value,
                                                                // Trocar a forma zera o que era específico da anterior.
                                                                cardFlag: null,
                                                                receivedMoney: '0,00',
                                                                moneyChange: '0,00'
                                                            })}
                                                            shortCardLabels
                                                            showCourtesy
                                                            hideSubscription={false}
                                                            onlySubscription={isPaidOnlyByPlan}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                {
                                                    // O valor por forma só existe no pagamento dividido: com
                                                    // uma forma só, ela cobre o total da venda.
                                                    isSplitPayment ?
                                                        <Col className="col-md-5">
                                                            <FormGroup className="mb-0">
                                                                <span className="payment-row__label">{t('value')}</span>
                                                                <InputDecimal
                                                                    name={`paymentValue-${row.key}`}
                                                                    value={row.value}
                                                                    onChange={(e) => updatePaymentRow(row.key, {
                                                                        value: e.target.value,
                                                                        // O troco depende do valor da forma.
                                                                        moneyChange: convertDoubleToStringPTBR(
                                                                            parseDecimal(row.receivedMoney) - parseDecimal(e.target.value))
                                                                    })}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        :
                                                        <></>
                                                }
                                            </Row>

                                            {
                                                isCashRow(row) ?
                                                    <Row className="g-2 mt-1">
                                                        <Col className="col-md-6">
                                                            <FormGroup className="mb-0">
                                                                <span className="payment-row__label">
                                                                    <i className="fas fa-hand-holding-usd me-2"></i>
                                                                    {t('money_received')}
                                                                </span>
                                                                <InputDecimal
                                                                    name={`moneyReceived-${row.key}`}
                                                                    value={row.receivedMoney}
                                                                    onChange={(e) => {
                                                                        const received = parseDecimal(e.target.value);
                                                                        const currentValue = parseDecimal(row.value);
                                                                        const isDefaultSplitValue = isSplitPayment
                                                                            && (Math.abs(currentValue - payableValue) < 0.01
                                                                                || Math.abs(currentValue - parseDecimal(row.receivedMoney)) < 0.01);
                                                                        // A linha nova começa com o total inteiro. Se o operador informa
                                                                        // primeiro que recebeu só parte em dinheiro, convertemos essa linha
                                                                        // para a parte recebida e deixamos o restante para a outra forma.
                                                                        const nextValue = isDefaultSplitValue
                                                                            && received > 0
                                                                            && received <= payableValue
                                                                            ? e.target.value
                                                                            : row.value;
                                                                        updatePaymentRow(row.key, {
                                                                            receivedMoney: e.target.value,
                                                                            value: nextValue,
                                                                            moneyChange: convertDoubleToStringPTBR(received - parseDecimal(nextValue))
                                                                        });
                                                                    }}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col className="col-md-6">
                                                            <FormGroup className="mb-0">
                                                                <span className="payment-row__label">
                                                                    <i className="fas fa-coins me-2"></i>
                                                                    {t('money_change')}
                                                                </span>
                                                                <InputDecimal
                                                                    name={`moneyChange-${row.key}`}
                                                                    // Troco negativo é uma inconsistência de preenchimento, não um valor que
                                                                    // deva aparecer como troco para o cliente. A validação continua usando o
                                                                    // valor negativo armazenado para orientar o ajuste antes da confirmação.
                                                                    value={parseDecimal(row.moneyChange) < 0 ? '0,00' : row.moneyChange}
                                                                    disabled="false"
                                                                />
                                                                {parseDecimal(row.moneyChange) > 0 && (
                                                                    <small className="order-payment-modal__change-feedback d-block mt-2">
                                                                        <i className="fas fa-check-circle me-1"></i>
                                                                        {t('change_to_return')}: <strong>{t('currency_symbol')} {row.moneyChange}</strong>
                                                                    </small>
                                                                )}
                                                            </FormGroup>
                                                        </Col>

                                                        {/* A mensagem explica como corrigir a linha; o campo de troco acima
                                                            mostra zero, pois troco negativo não é um valor válido. */}
                                                        {parseDecimal(row.moneyChange) < 0 && (
                                                            <Col className="col-12">
                                                                <div className="order-payment-modal__change-warning mt-2">
                                                                    <p className="order-payment-modal__change-warning-title">
                                                                        <i className="fas fa-triangle-exclamation me-2"></i>
                                                                        {t('negative_change_title')}
                                                                    </p>
                                                                    <p className="mb-0">
                                                                        {t('negative_change_hint', {
                                                                            received: `${t('currency_symbol')} ${row.receivedMoney}`
                                                                        })}
                                                                    </p>
                                                                </div>
                                                            </Col>
                                                        )}
                                                    </Row>
                                                    :
                                                    <></>
                                            }

                                            {
                                                isCardRow(row) ?
                                                    <Row className="g-2 mt-1">
                                                        <Col className="col-md-12">
                                                            <FormGroup className="mb-0">
                                                                <span className="payment-row__label">
                                                                    <i className="fas fa-building me-2"></i>
                                                                    {t('flag')}
                                                                </span>
                                                                <CardFlagSelect
                                                                    value={row.cardFlag}
                                                                    onChange={(value) => updatePaymentRow(row.key, { cardFlag: value })}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    </Row>
                                                    :
                                                    <></>
                                            }
                                        </div>

                                        {
                                            isSplitPayment ?
                                                <Button
                                                    variant="link"
                                                    className="payment-row__remove text-danger"
                                                    title={t('remove')}
                                                    onClick={() => removePaymentRow(row.key)}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </Button>
                                                :
                                                <></>
                                        }
                                    </div>
                                );
                            })}

                            <div className="order-payment-modal__split-actions">
                                {
                                    payments.length < MAX_PAYMENT_ROWS ?
                                        <Button variant="link" className="p-0" onClick={addPaymentRow}>
                                            <i className="fas fa-plus me-2"></i>
                                            {t('split_payment')}
                                        </Button>
                                        :
                                        // No teto, o botão sai em vez de ficar desabilitado: um
                                        // botão morto na tela faz o operador tentar clicar.
                                        <span></span>
                                }
                                {
                                    // Enquanto a soma não fecha o total, mostra o que falta (ou o
                                    // quanto passou), em vez de só recusar na confirmação.
                                    isSplitPayment && Math.abs(remainingToSplit) > 0.01 ?
                                        <span className={`order-payment-modal__split-remaining ${remainingToSplit < 0 ? 'is-over' : ''}`}>
                                            {remainingToSplit > 0 ? t('remaining_to_allocate') : t('exceeds_order_total')}:
                                            <strong className="ms-1">
                                                {t('currency_symbol')} {convertDoubleToStringPTBR(Math.abs(remainingToSplit))}
                                            </strong>
                                        </span>
                                        :
                                        <></>
                                }
                            </div>
                        </div>
                        }

                    </div>
                }
                size="lg"
                dialogClassName="order-payment-modal__dialog"
                btnOnClick={() => { onConfirm() }}
                btnDisabled={sending}
                btnText={
                    <span>
                        <i className="fas fa-check-circle me-2"></i>
                        {t("confirm_payment")}
                    </span>
                }
            />
            <NewPage
                motherMenu={t('sales')}
                childMenu={t('sales')}
                activeMenu={t('new')}
                child={
                    <Form
                        key={formVersion}
                        formik={formik}
                        entityData={entityData}
                        setParentItems={setItems}
                        setPlanLookupState={setPlanLookupState}
                        resetForm={resetForm}
                        setOrderTotalValue={setOrderTotalValue}
                        setOrderDiscount={setOrderDiscount}
                        setParentAppointmentId={setAppointmentId}
                        onOpenQuickCustomer={() => setQuickCustomerOpen(true)} />}
                setEntityData={setEntityData}
                path={"/sales/orders"}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.sales.order.route}
            />
            <CustomerQuickCreateModal
                show={quickCustomerOpen}
                onHide={() => setQuickCustomerOpen(false)}
                onCreated={(customerOption) => {
                    formik.setFieldValue('customer', customerOption);
                    setQuickCustomerOpen(false);
                }}
            />
        </>
    );
}

export default OrderNew;
